import { Nullable } from '@/scripts/models/utility-types';
import analytic from '..';
import { AnalyticPayload } from '../types';

const extractElementPath = (target: Nullable<HTMLElement>): string => {
  if (!target) return '';

  const path = [];
  let el: Nullable<HTMLElement> = target;
  while (el !== null && el !== document.body) {
    path.push(el);
    el = el.parentElement;
  }

  const acc: string[] = [];
  for (const element of path) {
    if (element.id) {
      acc.push(`#${element.id}`);
      break;
    }

    const classNameString = element.className
      .split(' ')
      .map(x => x.trim())
      .filter(x => x)
      .sort()
      .join('.')
    ;
    acc.push(
      [
        element.tagName,
        classNameString,
      ]
        .filter(x => x)
        .join('.')
      ,
    );
  }

  return acc.join('/');
};

const normalizeUrlString = (urlString: string): string => {
  if (urlString.startsWith('/')) {
    return `${location.origin}${urlString}`;
  }
  return urlString;
};

const checkIsInternalRedirection = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    return location.host === url.host;
  } catch (_e) {
    return false;
  }
};

const extractEventData = (event: Event): Nullable<AnalyticPayload> => {
  const target = event.target as Nullable<HTMLElement>;

  if (target == null) return null;

  const href = target.getAttribute('href');
  if (!href) return null;

  const urlString = normalizeUrlString(href);

  return {
    category: 'outbound',
    action: 'click-link',
    label: urlString,
    property: JSON.stringify({
      text: (target.textContent || '').trim(),
      path: extractElementPath(target),
      // eslint-disable-next-line @typescript-eslint/camelcase
      is_internal: checkIsInternalRedirection(urlString),
    }),
  };
};

const trackClick = (event: Event): void => {
  const analyticPayload = extractEventData(event);
  if (!analyticPayload) return;

  analytic.send(analyticPayload);
};

/**
 *
 * @param target
 * @return a handler when executed will remove the event listener from the supplied target
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const initializeTracker = (target: HTMLElement | Window = window) => {
  target.addEventListener('click', trackClick);
  return (): void => {
    target.removeEventListener('click', trackClick);
  };
};
